import React from 'react'
import PropTypes from 'prop-types'
import { useMutation } from '@apollo/client'
import { useIntl } from 'react-intl'
import { Row, Col } from 'antd'
import { useWatch, useFormContext } from 'react-hook-form'

import FormItem from 'components/Form/Item'
import Button from 'components/Button'
import FormattedMessage from 'components/FormattedMessage'
import FormSection from 'components/FormSection'

import ProjectsDropdown from 'components/ProjectsDropdown'
import { sanitizeFormValues, showSanitizedErrors } from 'components/ContactRequestFormComponents/helpers'

import EventDetailsOverview from './EventDetailsOverview'

import { closeErrorNotifications } from 'helpers/errors'
import { getGoogleAnalyticsTracker } from 'helpers/googleAnalytics'
import { createProjectUrl } from 'helpers/nexus'

import useBool from 'hooks/bool'

import { CREATE_CONTACT_REQUEST_MUTATION } from '../graphql'

import './index.less'

const AddToProject = (props) => {
  const {
    currentUser,
    initialData,
    provider,
    onSuccessCallback,
    projects,
    showCreateNewProjectLink,
  } = props

  const { handleSubmit } = useFormContext()
  const intl = useIntl()
  const [createContactRequest, { loading: contactRequestSubmitting }] = useMutation(CREATE_CONTACT_REQUEST_MUTATION)
  const [isFormDisabled, disableForm, enableForm] = useBool()

  const selectedProjectId = useWatch({ name: 'projectId' })
  const projectId = currentUser.isSignedIn
    ? selectedProjectId || initialData?.projectId
    : projects[0].id

  const isDashboardIntegratedWithNexus = currentUser?.preferredPartner?.settings.isDashboardIntegratedWithNexus

  const submitForm = (formData) => {
    const ga = getGoogleAnalyticsTracker()

    createContactRequest({
      variables: {
        providerId: provider.id,
        projectId,
        proposed: formData.proposed,
        googleAnalyticsClientId: ga && ga.get('clientId'),
        forNewProject: false,
      },
    }).then(({ data }) => {
      onSuccessCallback(data.createContactRequest.contactRequest)
    }).catch((errors) => {
      showSanitizedErrors(
        errors.graphQLErrors[0]?.extensions?.errors,
        intl,
      )
    })
  }

  const submitFormAction = (values) => {
    closeErrorNotifications()
    submitForm(sanitizeFormValues(values))
  }

  return (
    <form onSubmit={handleSubmit(submitFormAction)} className="contact-request__add-to-project">
      <FormSection label={<FormattedMessage id="contactRequestForm.selectEvent" />}>
        {currentUser.isSignedIn ? (
          <FormItem
            name="projectId"
            className="contact-request-form__event-selector"
            label={
              <FormattedMessage id={
                currentUser.isConsultant ? 'contactRequestForm.selectEvent' : 'requestForm.addToEvent'
              } />
            }
          >
            <ProjectsDropdown initialValue={projectId} currentUser={currentUser} withEmptyOptions />
          </FormItem>
        ) : (
          <div className="contact-request__add-to-project__project-title">
            {projects[0].title}
          </div>
        )}

        {showCreateNewProjectLink &&
          <a className="link" href={createProjectUrl(provider.id)}>
            <FormattedMessage id="contactRequest.formSwitcher.switch.newProject" />
          </a>
        }
      </FormSection>

      <FormSection label={<FormattedMessage id="contactRequest.sections.eventDetails" />}>
        <EventDetailsOverview
          projectId={projectId}
          provider={provider}
          disableForm={disableForm}
          enableForm={enableForm}
          isDashboardIntegratedWithNexus={isDashboardIntegratedWithNexus}
        />
      </FormSection>

      <FormSection>
        <Row>
          <Col>
            <Button
              translationKey="contactRequestForm.withExtendedRequestForm.cta"
              type="primary"
              size="large"
              htmlType="submit"
              className="contact-request__form__submit ant-btn--with-border-radius-small"
              disabled={contactRequestSubmitting || isFormDisabled}
            />
          </Col>
        </Row>
      </FormSection>
    </form>
  )
}

AddToProject.propTypes = {
  projects: PropTypes.arrayOf(PropTypes.object),
  currentUser: PropTypes.object,
  onSuccessCallback: PropTypes.func.isRequired,
  showCreateNewProjectLink: PropTypes.bool,
  initialData: PropTypes.shape({
    projectId: PropTypes.number,
  }),
  provider: PropTypes.shape({
    type: PropTypes.string,
    id: PropTypes.string.isRequired,
    eventCategories: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string,
    })),
  }).isRequired,
}

export default AddToProject
